import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, RefreshCw, Zap, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

/**
 * LeadReferencePanel - Collapsible panel showing WTF lead data summary
 *
 * Displays per-source lead breakdown, IVR call summary, and sync timestamp.
 * Allows TL to auto-fill pulse grid with WTF data via Auto-Fill button.
 */
export default function LeadReferencePanel({
  suggestions,
  loading,
  onAutoFill,
  onRefresh,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate relative time for sync timestamp
  const syncedAgo = useMemo(() => {
    if (!suggestions?.synced_at) return null;
    try {
      const syncTime = new Date(suggestions.synced_at);
      const now = new Date();
      const diffMinutes = Math.floor((now - syncTime) / 1000 / 60);
      if (diffMinutes < 1) return 'just now';
      if (diffMinutes === 1) return '1 min ago';
      if (diffMinutes < 60) return `${diffMinutes} min ago`;
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours === 1) return '1 hour ago';
      if (diffHours < 24) return `${diffHours} hours ago`;
      return `${Math.floor(diffHours / 24)} days ago`;
    } catch (e) {
      return 'unknown';
    }
  }, [suggestions?.synced_at]);

  // Check if data is stale (> 10 minutes old)
  const isStale = useMemo(() => {
    if (!suggestions?.synced_at) return false;
    try {
      const syncTime = new Date(suggestions.synced_at);
      const now = new Date();
      return Math.floor((now - syncTime) / 1000 / 60) > 10;
    } catch (e) {
      return false;
    }
  }, [suggestions?.synced_at]);

  // Totals for expanded table (must be before early returns for hooks consistency)
  const totalLeads = suggestions?.total_leads || 0;
  const sourceCount = suggestions?.sources?.length || 0;
  const hasUnmappedSources = suggestions?.unmapped_sources && suggestions.unmapped_sources.length > 0;

  const totals = useMemo(() => {
    if (!suggestions?.sources) return {};
    return {
      leads: totalLeads,
      visit_scheduled: suggestions.sources.reduce((s, src) => s + (src.suggestions?.visit_scheduled || 0), 0),
      visit_done: suggestions.sources.reduce((s, src) => s + (src.suggestions?.visit_done || 0), 0),
      closure_expected: suggestions.sources.reduce((s, src) => s + (src.suggestions?.closure_expected || 0), 0),
      revenue: suggestions.sources.reduce((s, src) => s + (src.suggestions?.revenue_expected || 0), 0),
    };
  }, [suggestions, totalLeads]);

  const formatRevenue = (val) => val > 0 ? `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val)}` : '-';

  // Loading state
  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading WTF lead data...</span>
        </div>
        <div className="mt-3 space-y-2">
          <div className="h-5 bg-blue-100 rounded w-full animate-pulse" />
          <div className="h-5 bg-blue-100 rounded w-3/4 animate-pulse" />
          <div className="h-5 bg-blue-100 rounded w-1/2 animate-pulse" />
        </div>
      </div>
    );
  }

  // Empty state
  if (!suggestions || suggestions.cache_miss || !suggestions.sources || suggestions.sources.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span>No WTF lead data available for this gym today. Data syncs every 5 minutes.</span>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  // Collapsed state
  if (!isExpanded) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <span className="font-semibold text-gray-800">WTF Lead Data</span>
              <span className="status-badge success ml-2">{totalLeads} leads</span>
              <span className="text-sm text-gray-500 ml-2">across {sourceCount} source{sourceCount !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Clock className="h-3 w-3" />
              <span>Synced {syncedAgo}</span>
              {isStale && <span className="text-amber-600 font-medium ml-1">(stale)</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasUnmappedSources && (
              <span className="status-badge warning">{suggestions.unmapped_sources.length} unmapped</span>
            )}
            <Button variant="outline" size="sm" onClick={() => setIsExpanded(true)} className="cursor-pointer">
              <ChevronDown className="h-4 w-4 mr-1" />
              Expand
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div className="bg-white border border-green-200 rounded-lg mb-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-200">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <h3 className="font-semibold text-gray-800">WTF Lead Data - Verified from Source</h3>
          <span className="status-badge info">{suggestions.gym_name || 'Unknown Gym'}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setIsExpanded(false)} className="cursor-pointer text-gray-500 hover:text-gray-700">
          <ChevronUp className="h-4 w-4 mr-1" />
          Collapse
        </Button>
      </div>

      {/* Source breakdown table */}
      <div className="px-4 pt-3">
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th style={{ textAlign: 'center' }}>Total Leads</th>
                <th style={{ textAlign: 'center' }}>Visit Scheduled</th>
                <th style={{ textAlign: 'center' }}>Visit Done</th>
                <th style={{ textAlign: 'center' }}>Closure Expected</th>
                <th style={{ textAlign: 'right' }}>Revenue Expected</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.sources.map((source, idx) => {
                const sugg = source.suggestions || {};
                return (
                  <tr key={idx}>
                    <td>
                      <span className="font-semibold">{source.source_id}</span>
                      <span className="text-gray-400 ml-1 text-xs">({source.cohort})</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="font-semibold text-blue-600">{source.wtf_lead_count || 0}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{sugg.visit_scheduled || 0}</td>
                    <td style={{ textAlign: 'center' }}>{sugg.visit_done || 0}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={sugg.closure_expected > 0 ? 'font-semibold text-green-600' : ''}>
                        {sugg.closure_expected || 0}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={sugg.revenue_expected > 0 ? 'font-semibold text-green-600' : 'text-gray-400'}>
                        {formatRevenue(sugg.revenue_expected || 0)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9fafb' }}>
                <td>TOTAL</td>
                <td style={{ textAlign: 'center', color: '#2563eb' }}>{totals.leads}</td>
                <td style={{ textAlign: 'center' }}>{totals.visit_scheduled}</td>
                <td style={{ textAlign: 'center' }}>{totals.visit_done}</td>
                <td style={{ textAlign: 'center', color: '#16a34a' }}>{totals.closure_expected}</td>
                <td style={{ textAlign: 'right', color: '#16a34a' }}>{formatRevenue(totals.revenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* IVR summary */}
      <div className="mx-4 mt-3 p-2.5 bg-gray-50 rounded-lg border text-sm">
        {suggestions.ivr_summary ? (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-700">IVR Calls ({suggestions.pulse_id})</span>
            <span className="text-gray-400">{suggestions.ivr_summary.pulse_window || 'N/A'}:</span>
            <span className="font-semibold text-blue-600">{suggestions.ivr_summary.total_dialed || 0} dialed</span>
            <span className="text-gray-300">|</span>
            <span className="font-semibold text-green-600">{suggestions.ivr_summary.total_connected || 0} connected</span>
          </div>
        ) : (
          <span className="text-gray-400 text-xs">IVR data unavailable for this pulse</span>
        )}
      </div>

      {/* Sync timestamp */}
      <div className="flex items-center justify-between px-4 mt-3">
        <div className="flex items-center gap-2 text-xs">
          <Clock className="h-3 w-3 text-gray-400" />
          <span className="text-gray-500">Synced from WTF API:</span>
          <span className={`font-semibold ${isStale ? 'text-amber-600' : 'text-green-600'}`}>
            {syncedAgo}
          </span>
          {isStale && <span className="text-amber-600">- Data may be stale, click Refresh</span>}
        </div>
        {hasUnmappedSources && (
          <span className="status-badge warning">{suggestions.unmapped_sources.length} unmapped sources</span>
        )}
      </div>

      {/* Unmapped sources warning */}
      {hasUnmappedSources && (
        <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="font-semibold text-amber-800">
                {suggestions.unmapped_sources.length} WTF source{suggestions.unmapped_sources.length === 1 ? '' : 's'} not mapped:
              </div>
              <div className="text-amber-700 mt-0.5">{suggestions.unmapped_sources.join(', ')}</div>
              <div className="text-gray-500 mt-1">Ask admin to configure in Master Config → Lead Source Mappings</div>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 px-4 py-3">
        <Button
          onClick={onAutoFill}
          disabled={!suggestions.sources || suggestions.sources.length === 0}
          size="sm"
          className="cursor-pointer"
        >
          <Zap className="h-4 w-4 mr-1" />
          Auto-Fill from WTF Data
        </Button>
        <Button variant="outline" size="sm" onClick={onRefresh} className="cursor-pointer">
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
        <span className="ml-auto text-xs text-gray-400">Click Auto-Fill to populate grid with verified WTF data</span>
      </div>
    </div>
  );
}
